import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from "@react-oauth/google";

import Link from "../components/core/link";
import LanguageToggle from "../components/core/LanguageToggle";
import SchoolRegisterFormModal from "../components/modals/SchoolRegisterFormModal";
import AppLogo from "../../assets/icons/iconShanenLi.svg";
import { LoaderComponent } from "../components/core/LoaderComponent";
import { loginWithGoogle, loginAsGuest, validateToken, getUserData } from "../../services/api";
import { clearAuthData } from "../../services/api/axios-instance";
import { useStoreByName } from "../../hooks/use-stores.hooks";

import { Button, Box, Typography, Tooltip } from "@mui/material";
import { Person } from "@mui/icons-material";
import { styled } from "@mui/system";

// import Google from "@mui/icons-material/Google";
import Google from "../../assets/icons/google_icon.png";
import BrowserModal from "../components/modals/BrowserModal";
import ClassCodeLoginModal from "../components/auth/ClassCodeLoginModal";
import BackgroundCircle from "../../assets/SVGComponents/BackgroundCircle";

function Copyright(props: any) {
  const { t } = useTranslation();
  return (
    <Typography
      className="copyright"
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {t('copyright')}
      <Link color="inherit">
        {t('appName')}
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const Icon = styled("img")({
  width: "24px",
  height: "24px",
  marginRight: "8px",
  display: "flex",
});

const AuthPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { uiStore } = useStoreByName('uiStore');

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [browserMessage, setBrowserMessage] = useState("");
  const [classLoginOpen, setClassLoginOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // פונקציה לקביעת הניווט לפי תפקיד המשתמש
  const navigateBasedOnRole = (userData: any) => {
    const userRoleId = userData?.personalInfo?.role?.id;
    
          switch (userRoleId) {
      case 4: // מנהל בית ספר
        navigate("/admin-panel-panel");
        break;
      case 3: // מנהל מערכת
        navigate("/admin-panel");
        break;
      case 1: // מורה
      case 2: // תלמיד
      default:
        navigate("/home");
        break;
    }
  };

  const handleGoogleLoginSuccess = async (tokenResponse: {
    access_token: string;
  }) => {
    
    setIsAuthenticating(true);
    
    try {
      const response = await loginWithGoogle(tokenResponse.access_token);
      
      if (response?.token) {
        localStorage.setItem("access_token", response.token);
        uiStore?.setIsGuestUser(false);
        
        try {
          const userDataResponse = await getUserData();
          navigateBasedOnRole(userDataResponse);
        } catch (userError) {
          console.error("User data fetch failed:", userError);
          setIsAuthenticating(false);
        }
      } else {
        console.error("No token in server response");
        setIsAuthenticating(false);
      }
    } catch (error: any) {
      console.error("Server login failed:", error?.message);
      console.error("Error status:", error?.response?.status);
      console.error("Error data:", error?.response?.data);
      setIsAuthenticating(false);
    }
  };

  // Students sign in with a class code and a PIN. Same JWT as every other route.
  const handleClassCodeAuthenticated = async (token: string) => {
    setIsAuthenticating(true);
    setClassLoginOpen(false);
    localStorage.setItem("access_token", token);
    uiStore?.setIsGuestUser(false);

    try {
      const userDataResponse = await getUserData();
      navigateBasedOnRole(userDataResponse);
    } catch (userError) {
      console.error("User data fetch failed:", userError);
      setIsAuthenticating(false);
    }
  };

  const handleGuestLogin = async () => {
    
    try {
      const response = await loginAsGuest();
      
      if (response?.token) {
        localStorage.setItem("access_token", response.token);
        uiStore?.setIsGuestUser(true);
        
        try {
          await getUserData();
          navigate("/home");
        } catch (userError) {
          console.error("Guest user data fetch failed:", userError);
          navigate("/home");
        }
      } else {
        console.error("No token in guest login response");
      }
    } catch (error: any) {
      console.error("Guest login failed:", error?.message);
      console.error("Error status:", error?.response?.status);
      console.error("Error data:", error?.response?.data);
    }
  };

  const login = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: (error: any) => {
      console.error("Google Login Failed:", error);
      console.error("Google Login Error Details:", {
        error: error?.error,
        error_description: error?.error_description,
        error_uri: error?.error_uri
      });
    },
    onNonOAuthError: (error: any) => {
      console.error("Google Non-OAuth Error:", error);
    },
    scope: "openid email profile",
    flow: "implicit"
  });

  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          await validateToken();
          // קבלת נתוני המשתמש וניווט לפי התפקיד
          try {
            const userDataResponse = await getUserData();
            navigateBasedOnRole(userDataResponse);
          } catch (error) {
            console.error("Error fetching user data:", error);
            // אם לא הצלחנו לקבל נתונים (שרת לא זמין), נקה את נתוני האימות ונשאר בדף האימות
            clearAuthData();
          }
        } catch (e) {
          console.error(`error in validate: ${e}`);
          // טוקן לא תקף - נקה נתוני אימות ונשאר בדף האימות
          clearAuthData();
        }
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, [navigate]);

  // Browser detection logic
  useEffect(() => {
    if (!isLoading) {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("android")) {
        setBrowserMessage(t('androidMessage'));
        handleOpen();
      } else if (userAgent.includes("edg/")) {
        setBrowserMessage(t('edgeMessage'));
        handleOpen();
      }
    }
  }, [isLoading]);

  if (isLoading || isAuthenticating) {
    return <LoaderComponent />;
  }

  return (
    <div className="app-container">
      <BackgroundCircle />
      <div className="body auth-page">
        <div className="card">
          <div className="brand-container">

            <div className="lang-button">
              <LanguageToggle />
            </div>

            <img src={AppLogo} alt="הלוגו של שנן לי" />
            <h3>{t('appName')}</h3>
            <p>{t('appDescription')}</p>
          </div>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={
                <Box 
                  component="span"
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginInlineEnd: '8px',
                  }}
                >
                  <Icon src={Google} alt={t('googleIcon')} />
                </Box>
              }
              size="medium"
              className="button google"
              onClick={() => login()}
              sx={{
                backgroundColor: '#4285F4',
                '&:hover': {
                  backgroundColor: '#357AE8',
                },
                boxShadow: '0 2px 8px rgba(66, 133, 244, 0.3)',
              }}
            >
              {t('loginWithGoogle')}
            </Button>
            
            <Button
              variant="contained"
              size="medium"
              className="button class-code"
              onClick={() => setClassLoginOpen(true)}
              sx={{
                padding: '8px 16px',
                backgroundColor: '#2e7d32',
                '&:hover': { backgroundColor: '#1b5e20' },
                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
              }}
            >
              {t('joinWithClassCode')}
            </Button>

            <Button
              variant="outlined"
              size="medium"
              className="button guest"
              onClick={handleGuestLogin}
              
              sx={{
                padding: '8px 16px',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: 'text.secondary',
                textAlign: 'center',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.4)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {t('loginAsGuest')}
            </Button>
          </Box>
          
          <h5>
            {t('schoolRegistrationInfo')}
            <br />
            <Link onClick={handleClickOpen}>{t('clickHere')}</Link>
          </h5>
        </div>
        <Copyright />
      </div>

      <ClassCodeLoginModal
        open={classLoginOpen}
        onClose={() => setClassLoginOpen(false)}
        onAuthenticated={handleClassCodeAuthenticated}
      />

      <SchoolRegisterFormModal open={open} handleClose={handleClose} />
      {/* <BrowserModal
        open={openModal}
        handleClose={handleClose}
        browserMessage={browserMessage}
      /> */}
    </div>
  );
};

export default observer(AuthPage);
